import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

// Doit refléter core/rbac.py SENSITIVE_PERMISSIONS : le wildcard '*' NE satisfait PAS ces permissions.
const SENSITIVE_PERMISSIONS = new Set(["control:attest", "risk:accept", "tenant:read", "tenant:provision", "twin:ingest"]);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, obj = authed

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout best-effort : même si l'appel réseau échoue, on force l'état anonyme côté client.
      console.debug("logout: échec de l'appel /auth/logout (ignoré)", e?.message);
    }
    setUser(false);
  };

  // Parité RBAC backend : permissions SENSIBLES non couvertes par le wildcard '*'
  // (core/rbac.py SENSITIVE_PERMISSIONS). Un grant EXPLICITE est requis (ex. CISO pour control:attest).
  const can = (perm) =>
    !!user &&
    (user.permissions?.includes(perm) ||
      (!SENSITIVE_PERMISSIONS.has(perm) && user.permissions?.includes("*")));

  return (
    <AuthContext.Provider value={{ user, login, logout, refresh, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
